import { Controller } from "@hotwired/stimulus"

// The citation list of one layoff row, upgraded from a disclosure to a modal.
//
// ## What is in the server response, and why this only ever upgrades it
//
// Each row of the table at /layoffs ships a `<details>` holding that row's
// sources. That is the product, not a fallback: the whole pitch of this tracker
// is that a reader can check us, and evidence that needs a working bundle to
// reach is evidence withheld from whoever has the worst connection. So the
// markup works on its own, and everything below is an improvement on top of
// markup that was already finished.
//
// Three consequences, and each one is a rule rather than a preference:
//
//   1. **The list is MOVED, never copied.** There is exactly one `<ul>` per row
//      in the document at all times, the one the server sent. A copy would be a
//      second statement of the row's evidence that could drift from the first,
//      which is the thing this product cannot afford to have happen quietly.
//   2. **The dialog is built here, not in the template.** The server response is
//      then unchanged, so a reader with no script gets precisely the markup the
//      component spec asserts, and the page carries no `<h2>` per row for a
//      heading nobody can see.
//   3. **The summary's default is prevented only once the modal is certain.**
//      A `preventDefault()` with nothing behind it is a control that swallows
//      the click and shows nothing, which is worse than the disclosure it
//      replaced. Every early return below happens BEFORE that call.
//
// ## One dialog per page, and the list moves in and out of it
//
// The alternative was a dialog per row. This is the simpler thing to read:
// there is one element to build, one to style, one to tear down, and the
// "put it back" step has one place to put it back to. A row's `<ul>` is the
// last child of its `<details>`, after the `<summary>`, so returning it is an
// ordinary `appendChild` and no placeholder node is needed to remember where it
// came from.
//
// ## showModal(), and what is deliberately not written here
//
// `dialog_controller.js` is the house wrapper over `<dialog>` and its reasoning
// applies unchanged: `showModal()` gives focus trapping, Escape, inertness of
// the rest of the page and the top layer, and hand-rolled versions get the
// focus handling wrong. It is not reused directly because it expects its
// `<dialog>` to already be in the document and this one does not exist until a
// reader asks for it. The backdrop geometry check below is taken from it
// rather than reinvented.
export default class extends Controller {
  connect() {
    this.onClose = this.onClose.bind(this)
    this.onBackdropClick = this.onBackdropClick.bind(this)
    this.tearDown = this.tearDown.bind(this)
    document.addEventListener("turbo:before-cache", this.tearDown)
  }

  // A disconnect with the dialog open would strand the row's only citation list
  // inside an element that is about to be orphaned, because the dialog lives on
  // <body> rather than inside this controller's element. Put the list back
  // first, then take the dialog away.
  disconnect() {
    document.removeEventListener("turbo:before-cache", this.tearDown)
    this.tearDown()
  }

  // Also run before Turbo copies the page into its cache, which happens before
  // the disconnect. Left alone, the copy holds an open dialog nothing listens
  // to and a row whose list is inside it, and Back restores exactly that. The
  // list goes back first and synchronously, because a dialog's `close` event
  // arrives a task later, after the copy has been taken.
  tearDown() {
    this.returnList()
    this.dialog?.close()
    this.dialog?.remove()
    this.dialog = null
  }

  open(event) {
    const summary = event.currentTarget
    const disclosure = summary.closest("details")
    const list = disclosure?.querySelector(".layoff-sources__list")
    if (!list) return

    const dialog = this.dialogElement()
    if (!dialog) return

    // From here the modal is going to appear, so the disclosure must not.
    event.preventDefault()

    // "2 sources for Kade Biosciences", read off the summary rather than
    // restated, so the modal cannot end up naming a different row from the one
    // whose citations are in it.
    const label = summary.textContent.replace(/\s+/g, " ").trim()

    this.origin = disclosure
    this.opener = summary
    this.titleElement.textContent = label
    dialog.setAttribute("aria-label", label)
    this.bodyElement.appendChild(list)
    dialog.showModal()
    // The title rather than the close button, so the first thing announced is
    // whose sources these are and not the way out of them.
    this.titleElement.focus()
  }

  close() {
    this.dialog?.close()
  }

  // Fires for every way out: the close button, Escape, and a backdrop click.
  // Escape never reaches a handler of ours, which is the whole reason the
  // put-it-back step hangs off this event rather than off the button.
  onClose() {
    const opener = this.opener
    this.returnList()
    opener?.focus()
  }

  // Taken from dialog_controller#backdropClose. The dialog element fills the
  // backdrop area, so a click on the backdrop is a click whose coordinates fall
  // outside the dialog's own box.
  onBackdropClick(event) {
    const dialog = this.dialog
    if (!dialog || event.target !== dialog) return

    const box = dialog.getBoundingClientRect()
    const inside =
      event.clientX >= box.left && event.clientX <= box.right &&
      event.clientY >= box.top && event.clientY <= box.bottom

    if (!inside) dialog.close()
  }

  returnList() {
    if (!this.origin) return

    const list = this.bodyElement?.querySelector(".layoff-sources__list")
    // Back to the end of the `<details>`, which is where it started: the
    // `<summary>` is the only other child and it comes first.
    if (list) this.origin.appendChild(list)
    this.origin = null
    this.opener = null
  }

  // Built once, on the first row anybody opens, and kept for the rest of the
  // visit. Returns null on a browser with no modal dialog support, which is the
  // one case where the click has to be left alone to open the disclosure.
  dialogElement() {
    if (this.dialog) return this.dialog

    const dialog = document.createElement("dialog")
    if (typeof dialog.showModal !== "function") return null

    dialog.className = "layoff-sources__dialog"

    const head = document.createElement("div")
    head.className = "layoff-sources__dialog-head"

    // Named with `aria-label` on the dialog rather than `aria-labelledby` at
    // this heading. The dialog is built in script, so an id here would be an id
    // nothing in the server response issued, and a second table on one page
    // would quietly issue it twice. The two carry the same string, set together
    // on every open.
    //
    // `tabindex="-1"` so focus can be sent here on open. It is not a control
    // and it stays out of the tab order.
    const title = document.createElement("h2")
    title.className = "layoff-sources__dialog-title"
    title.tabIndex = -1

    const close = document.createElement("button")
    close.type = "button"
    close.className = "layoff-sources__dialog-close"
    close.setAttribute("aria-label", "Close")
    close.textContent = "×"
    close.addEventListener("click", () => this.close())

    const body = document.createElement("div")
    body.className = "layoff-sources__dialog-body"

    head.append(title, close)
    dialog.append(head, body)
    dialog.addEventListener("close", this.onClose)
    dialog.addEventListener("click", this.onBackdropClick)
    document.body.appendChild(dialog)

    this.dialog = dialog
    this.titleElement = title
    this.bodyElement = body
    return dialog
  }
}
